import React, { useState, useEffect, useCallback } from "react";
import { Steps, Form, Button, Card, Typography, message, Spin } from "antd";
import {
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getUserId } from "../utils/tokenUtils";
import { apiService } from "../utils/apiService";
import { API_CONFIG } from "../utils/constants";
import "../assets/styles/breakdownWizard.css";

// Import wizard step components
import ControllerStep from "../components/wizard/ControllerStep";
import DriverStep from "../components/wizard/DriverStep";
import LocationStep from "../components/wizard/LocationStep";
import VehicleStep from "../components/wizard/VehicleStep";
import DetailsStep from "../components/wizard/DetailsStep";
import SummaryStep from "../components/wizard/SummaryStep";
import ImagesStep from "../components/wizard/ImagesStep";

const { Step } = Steps;
const { Title } = Typography;

/**
 * Breakdown Request Wizard Component
 * Multi-step form for creating breakdown requests with comprehensive data collection
 */

const EXT_TO_MIME = {
  heic: "image/heic",
  heif: "image/heif",
}
const BreakdownWizard = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Controller Information
    controllerName: "",
    controllerContactNo: "",

    // Customer & Driver Information
    selectedCompany: null,
    selectedCompanyId: "",
    selectedDriverId: "",
    driverName: "",
    driverCell: "",

    // Location Information
    address: "",
    latitude: "",
    longitude: "",

    // Vehicle Information
    truckRegistrationNumber: "",
    truckFleetNumber: "",
    truckVinNumber: "",
    truckMake: "",
    truckModel: "",
    truckYear: "",
    mileage: "",
    leaseType: "",
    costCentreName: "",
    vehicleId: "",

    // Trailer Information
    trailer1Fault: false,
    trailer2Fault: false,
    trailer1Data: {},
    trailer2Data: {},

    // Order & Case Information
    orderNumber: "",
    internalCaseNotes: "",
    description: "",
    // Images (optional)
    imageryFiles: [],
  });

  const steps = [
    {
      icon: <UserOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <CarOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <EnvironmentOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <ToolOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <FileTextOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <PictureOutlined style={{ fontSize: "16px" }} />,
    },
    {
      icon: <CheckCircleOutlined style={{ fontSize: "16px" }} />,
    },
  ];

  /**
   * Handle form data updates from step components
   */
  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  /**
   * Move to next step with validation
   */
  const nextStep = async () => {
    try {
      await form.validateFields();
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (error) {
      message.error("Please complete all required fields before continuing");
    }
  };

  /**
   * Move to previous step
   */
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Submit the complete breakdown request
   */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const readFileAsDataURL = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      const getMediaTypeFromFile = (file) => {
        return file.type;
      };
      const dataURLToRawBase64 = (dataURL) => {
        if (typeof dataURL !== "string") return "";
        const commaIdx = dataURL.indexOf(",");
        return commaIdx >= 0 ? dataURL.slice(commaIdx + 1) : dataURL;
      };

      // Build imagery payload from selected files
      const imageryFiles = formData.imageryFiles || [];
      const originFiles = (imageryFiles || [])
        .map((f) => f.originFileObj)
        .filter(Boolean);
      // IMPORTANT: keep the original file encoding (especially for PNG).
      // Some backends reject or mishandle re-encoded (e.g. WebP) payloads.
      const imageryBase64 =
        originFiles.length > 0
          ? (
              await Promise.all(
                originFiles.map(async (f) => {
                  try {
                    const dataUrl = await readFileAsDataURL(f);
                    return dataURLToRawBase64(dataUrl);
                  } catch {
                    return null;
                  }
                }),
              )
            ).filter(Boolean)
          : [];
      const imageryModels =
        originFiles.length > 0
          ? (
              await Promise.all(
                originFiles.map(async (file) => {
                  const mediaType = getMediaTypeFromFile(file);
                  if (!mediaType) return null;

                  try {
                    const dataUrl = await readFileAsDataURL(file);
                    const base64 = dataURLToRawBase64(dataUrl);
                    if (!base64) return null;

                    return { 
                      Imagery: base64, 
                      MediaType: mediaType || EXT_TO_MIME[String(file.name.split(".").pop())], 
                      Extension: file.name.split(".").pop() };
                  } catch {
                    return null;
                  }
                }),
              )
            ).filter(Boolean)
          : [];
      // Prepare the payload according to the API specification
      const payload = {
        controllerName: formData.controllerName,
        controllerContactNo: formData.controllerContactNo,
        selectedDriverId: formData.selectedDriverId,
        driverName: formData.driverName,
        driverCell: formData.driverCell,
        address: formData.address,
        truckRegistrationNumber: formData.truckRegistrationNumber,
        truckFleetNumber: formData.truckFleetNumber,
        truckVinNumber: formData.truckVinNumber,
        truckMake: formData.truckMake,
        truckModel: formData.truckModel,
        truckYear: formData.truckYear,
        leaseType: formData.leaseType,
        orderNumber: formData.orderNumber,
        internalCaseNotes: formData.internalCaseNotes,
        trailerRegistrationNumber: formData.trailer1Data.registration,
        trailerFleet: formData.trailer1Data.fleet,
        trailerVinNumber: formData.trailer1Data.vin,
        trailerMake: formData.trailer1Data.make,
        trailerModel: formData.trailer1Data.model,
        trailerYear: formData.trailer1Data.year,
        secondTrailerRegistrationNumber: formData.trailer2Data.registration,
        secondTrailerFleet: formData.trailer2Data.fleet,
        secondTrailerVinNumber: formData.trailer2Data.vin,
        secondTrailerMake: formData.trailer2Data.make,
        secondTrailerModel: formData.trailer2Data.model,
        secondTrailerYear: formData.trailer2Data.year,
        description: formData.description,
        faultOnTrailer: formData.trailer1Fault ? 1 : 0,
        faultOnSecondTrailer: formData.trailer2Fault ? 1 : 0,
        addressFromLocation: formData.address,
        vehicleId: formData.vehicleId,
        trailerVehicleId: formData.trailer1Data.vehicleId,
        trailerLeaseType: formData.trailer1Data.leaseType,
        secondTrailerVehicleId: formData.trailer2Data.vehicleId,
        secondTrailerLeaseType: formData.trailer2Data.leaseType,
        OEMId: "",
        userId: getUserId(),
        latitude: formData.latitude,
        longitude: formData.longitude,
        companyName: formData.selectedCompany?.name || "",
        selectedCompany: formData.selectedCompany?.name || "",
        costCentreName: formData.costCentreName,
        warranty: "",
        attendingMechanic: "",
        breakdownDescription: formData.description,
        isVehicleLoadedTextVal: "",
        breakdownType: "1",
        otherBreakdownInfo: "",
        Imagery: imageryModels,
        breakdownRequestReference: formData.breakdownRequestReference || "",
        breakdownAddress: "",
        mileage: formData.mileage,
        driver: "",
        mobileNumber: "",
        breakdownClassType: "",
        equipmentFleetNumber: "",
        equipmentMake: "",
        equipmentModel: "",
        equipmentSerialNumber: "",
        equipmentYear: "",
        originatorReference: "PWA",
        companyId: formData.selectedCompany?.id || "",
      };

      const response = await apiService.post(
        API_CONFIG.BREAKDOWNS.CREATE_REQUEST,
        payload,
      );
      if (response) {
        message.success("Breakdown request created successfully!");
        navigate("/breakdowns");
      } else {
        throw new Error("Failed to create breakdown request");
      }
    } catch (error) {
      console.error("Error creating breakdown request:", error);
      message.error("Failed to create breakdown request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Render the current step component
   */
  const renderStepContent = () => {
    const stepProps = {
      form,
      formData,
      updateFormData,
      loading,
    };

    switch (currentStep) {
      case 0:
        return <ControllerStep {...stepProps} />;
      case 1:
        return <DriverStep {...stepProps} />;
      case 2:
        return <VehicleStep {...stepProps} />;
      case 3:
        return <LocationStep {...stepProps} />;
      case 4:
        return <DetailsStep {...stepProps} />;
      case 5:
        return <ImagesStep {...stepProps} />;
      case 6:
        return <SummaryStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: "16px", maxWidth: "800px", margin: "0 auto" }}>
      <Card>
        <Title level={2} style={{ textAlign: "center", marginBottom: "24px" }}>
          Create Breakdown Request
        </Title>

        <Steps
          current={currentStep}
          direction="horizontal"
          size="small"
          responsive={false}
          style={{
            marginBottom: "32px",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          {steps.map((step, index) => (
            <Step key={index} icon={step.icon} />
          ))}
        </Steps>

        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={formData}
            onValuesChange={(changedValues, allValues) => {
              updateFormData(changedValues);
            }}
          >
            {renderStepContent()}
          </Form>
        </Spin>

        <div
          style={{
            display: "flex",
            gap: "16px",
            marginTop: "32px",
          }}
        >
          <Button
            style={{ flex: 1 }}
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          {currentStep === steps.length - 1 ? (
            <Button
              type="primary"
              style={{ flex: 1 }}
              onClick={handleSubmit}
              loading={loading}
            >
              Generate Request
            </Button>
          ) : (
            <Button type="primary" style={{ flex: 1 }} onClick={nextStep}>
              Next
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BreakdownWizard;
